import whenDomReady from 'when-dom-ready';
import { tns } from 'tiny-slider/src/tiny-slider';
const ACTIVE = 'is-active';
const SINGLE = 'single-btn';
function initialise() {
    const sections = document.querySelectorAll('.js-tabbed-section');
    sections.forEach(initialiseSection);
}
export function initialiseSection(section) {
    let suppressHash = false;
    let initializing = true;
    let firstTransitionHandled = false;
    const rotateEnabled = section.classList.contains('js-tabbed-content-rotator');
    const widgetId = section.getAttribute('data-widget-id') || '';
    const nav = section.querySelector('.js-tabbed-nav');
    if (!nav)
        return;
    const track = section.querySelector('.js-tabbed-track');
    const accordionItems = section.querySelectorAll('.js-accordion-item');
    const navItems = nav.querySelector('.tabbed-section__nav-list');
    const ROTATION_DELAY = 7000;
    // Slider instance
    const instance = tns({
        arrowKeys: false,
        autoplay: false,
        center: true,
        items: 1,
        container: track,
        controls: false,
        gutter: 160,
        loop: false,
        mouseDrag: false,
        nav: false,
        touch: false,
        autoHeight: true,
    });
    instance.events.on('indexChanged', onIndexChanged);
    // Autoplay
    let autoplayInterval = null;
    const autoplayBtn = section.querySelector('.js-tabbed-autoplay');
    const autoplayBtnWrapper = section.querySelector('.tabbed-section__autoplay-btn-wrapper');
    const circle = section.querySelector('.progress-ring__circle');
    let circumference = 0;
    if (circle) {
        const r = circle.r.baseVal.value;
        circumference = 2 * Math.PI * r;
        circle.style.strokeDasharray = `${circumference}`;
        circle.style.strokeDashoffset = `${circumference}`;
        circle.style.setProperty('--length', `${circumference}`);
    }
    function restartProgressRing() {
        if (!circle)
            return;
        circle.style.animation = 'none';
        circle.getBoundingClientRect();
        circle.style.animation = `progress-ring ${ROTATION_DELAY}ms linear infinite`;
        circle.style.animationPlayState = 'running';
    }
    function clearProgressRing() {
        if (!circle)
            return;
        circle.style.animation = 'none';
        circle.style.strokeDashoffset = `${circumference}`;
    }
    function startAutoplay() {
        if (autoplayInterval !== null)
            return;
        restartProgressRing();
        autoplayInterval = window.setInterval(() => {
            suppressHash = true;
            const info = instance.getInfo();
            instance.goTo(info.index + 1 > info.slideCount - 1 ? 0 : info.index + 1);
            setTimeout(() => (suppressHash = false), 0);
        }, ROTATION_DELAY);
        autoplayBtn?.classList.add('is-playing');
        autoplayBtn?.classList.remove('is-paused');
    }
    function stopAutoplay() {
        if (autoplayInterval !== null) {
            clearInterval(autoplayInterval);
            autoplayInterval = null;
        }
        if (circle)
            circle.style.animationPlayState = 'paused';
        autoplayBtn?.classList.add('is-paused');
        autoplayBtn?.classList.remove('is-playing');
    }
    if (autoplayBtn) {
        autoplayBtn.addEventListener('click', () => {
            autoplayInterval !== null ? stopAutoplay() : startAutoplay();
        });
    }
    if (rotateEnabled && isDesktop()) {
        startAutoplay();
        autoplayBtnWrapper?.classList.remove('is-hidden');
    }
    else {
        stopAutoplay();
        autoplayBtnWrapper?.classList.add('is-hidden');
    }
    // Buttons
    const buttons = nav.querySelectorAll('button');
    if (buttons.length) {
        buttons.forEach((button) => {
            button.addEventListener('click', (e) => {
                suppressHash = true;
                stopAutoplay();
                clearProgressRing();
                onButtonClick(e);
                setTimeout(() => (suppressHash = false), 0);
            });
        });
        if (buttons.length === 1) {
            buttons.forEach((button) => button.classList.add(SINGLE));
        }
    }
    function onButtonClick(event) {
        event.preventDefault();
        const btn = event.currentTarget;
        const target = parseInt(btn.dataset.target || '0', 10);
        instance.goTo(target);
        clearProgressRing();
        accordionItems.forEach((a) => a.classList.remove(ACTIVE));
    }
    // Initialization with transitionEnd
    instance.events.on('transitionEnd', () => {
        if (firstTransitionHandled)
            return;
        firstTransitionHandled = true;
        initializing = false;
        requestAnimationFrame(() => {
            updateSliderFromHash(instance, widgetId, {
                navItems,
                allowScroll: true, // only on initial load
            });
        });
    });
    function onIndexChanged(info) {
        if (initializing)
            return;
        restartProgressRing();
        buttons.forEach((button) => button.classList.remove(ACTIVE));
        const activeButton = buttons[info.index];
        if (activeButton)
            activeButton.classList.add(ACTIVE);
        accordionItems.forEach((a) => a.classList.remove(ACTIVE));
        accordionItems.forEach((a) => {
            const content = a.querySelector('.js-accordion-content');
            if (content)
                calculateHeight(content);
        });
    }
    accordionItems.forEach((a) => {
        const content = a.querySelector('.js-accordion-content');
        if (content)
            calculateHeight(content);
    });
    // Unified deep‑link handling for initial load + later hash changes
    function updateSliderFromHash(instance, widgetId, options) {
        const { navItems, allowScroll } = options;
        const rawHash = window.location.hash;
        if (!rawHash || !rawHash.startsWith('#'))
            return;
        const hash = rawHash.substring(1);
        if (!hash.includes('='))
            return;
        const [id, handle] = hash.split('=');
        if (id !== widgetId)
            return;
        const button = document.querySelector(`.js-tabbed-section[data-widget-id="${widgetId}"] button[data-handle="${handle}"]`);
        if (!button)
            return;
        const index = parseInt(button.dataset.target || '0', 10);
        instance.goTo(index);
        if (allowScroll && navItems) {
            const navHeight = getNavHeight();
            const offset = getTopOffset(navItems) - navHeight - 32;
            window.scrollTo({ top: offset, behavior: 'smooth' });
        }
    }
    // React to URL changes (manual edits, browser back/forward).
    // Guard strictly to hashes that belong to THIS widget instance — any other
    // hash (e.g. '#success' from a BizForm redirect) must not be consumed here.
    window.addEventListener('hashchange', () => {
        if (suppressHash)
            return;
        // Only handle hashes in the format this widget owns: #<widgetId>=<handle>
        if (!window.location.hash.startsWith(`#${widgetId}=`))
            return;
        updateSliderFromHash(instance, widgetId, {
            navItems,
            allowScroll: true,
        });
    });
    // Resize handling
    window.addEventListener('resize', () => {
        if (rotateEnabled && isDesktop()) {
            startAutoplay();
            autoplayBtnWrapper?.classList.remove('is-hidden');
        }
        else {
            stopAutoplay();
            autoplayBtnWrapper?.classList.add('is-hidden');
        }
    });
}
// Utilities
function getTopOffset(el) {
    const rect = el.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    return rect.top + scrollTop;
}
function getNavHeight() {
    const mainNav = document.querySelector('.js-header');
    let navHeight = 150;
    if (mainNav)
        navHeight = mainNav.getBoundingClientRect().height;
    const subNav = document.querySelector('.js-subnav');
    if (subNav)
        navHeight += subNav.getBoundingClientRect().height;
    return navHeight;
}
function calculateHeight(el) {
    const current = el.style.maxHeight;
    el.style.maxHeight = 'none';
    const height = el.getBoundingClientRect().height;
    el.style.setProperty('--height', `${height}px`);
    el.style.maxHeight = current;
    return height;
}
function isDesktop() {
    return window.innerWidth >= 1024;
}
whenDomReady().then(initialise);
